/**
 * AdminAdvisor — ИИ-советник на Дашборде (роль admin/superadmin).
 *
 * Гибрид: цифры из GET /api/tasks/advisor (детерминированный SQL на бэке),
 * ИИ-строка `line` приходит только в состоянии live. Состояния:
 *   cold    — ничего не рисуем (онбординг закрывает <OnboardingChecklist>);
 *   warming — есть работы, но нет ни одного выставления → мост к первым деньгам;
 *   live    — денежный слой: метрики + ИИ-строка + карточки-действия.
 *
 * Все строки — через t() (i18n). PostHog: показ + клики по карточкам.
 * Иконки — инлайн-SVG (в проекте нет иконочного шрифта).
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { api } from '../api/client';
import { usePerm } from '../hooks/usePerm';
import { Analytics, track } from '../analytics';
import { formatMajor } from '../utils/money';
import CityLimitNote from './CityLimitNote';

// Русское склонение по числу: возвращает суффикс ключа _one/_few/_many.
// Учитывает 11–14 (→many) и десятки (21→one, 22→few). Для EN: one/множ.
function plf(n, lang) {
  if (lang === 'en') return n === 1 ? 'one' : 'many';
  const m10 = n % 10, m100 = n % 100;
  if (m100 >= 11 && m100 <= 14) return 'many';
  if (m10 === 1) return 'one';
  if (m10 >= 2 && m10 <= 4) return 'few';
  return 'many';
}

// tone → цвет иконки/акцента (переменные темы проекта)
const TONE = {
  danger:  'var(--red, #a32d2d)',
  amber:   'var(--orange, #854f0b)',
  info:    'var(--accent, #185fa5)',
  teal:    'var(--green, #0f6e56)',
  gray:    'var(--text2, #5f5e5a)',
  purple:  '#6366f1',
};

// Минимальный набор инлайн-SVG (stroke=currentColor — цвет наследуется).
const PATHS = {
  x:        'M6 6l12 12M18 6L6 18',
  sparkles: 'M12 3l1.9 4.6L18.5 9.5 13.9 11.4 12 16l-1.9-4.6L5.5 9.5l4.6-1.9zM18 14l1 2.4 2.4 1-2.4 1-1 2.4-1-2.4-2.4-1 2.4-1z',
  alert:    'M12 4l9 16H3zM12 10v4M12 17.5v.5',
  clock:    'M12 7v5l3 2M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  receipt:  'M5 3v18l2-1 2 1 2-1 2 1 2-1 2 1V3l-2 1-2-1-2 1-2-1-2 1zM8 8h8M8 12h8M8 16h5',
  gift:     'M20 12v9H4v-9M2 7h20v5H2zM12 22V7M12 7S11 3 8.5 3 6 5 6 6s1 1 6 1zM12 7s1-4 3.5-4S18 5 18 6s-1 1-6 1z',
  file:     'M14 3H6v18h12V7zM14 3v4h4M9 13h6M9 17h6',
  link:     'M10 13a5 5 0 007 0l2-2a5 5 0 00-7-7l-1 1M14 11a5 5 0 00-7 0l-2 2a5 5 0 007 7l1-1',
  eye:      'M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7zM12 9a3 3 0 100 6 3 3 0 000-6z',
};

function Ico({ name, size = 19 }) {
  const d = PATHS[name] || PATHS.file;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {d.split('M').filter(Boolean).map((seg, i) => <path key={i} d={'M' + seg} />)}
    </svg>
  );
}

function Card({ tone, ico, title, sub, cta, phId, onClick, onDismiss, dismissLabel }) {
  const color = TONE[tone] || TONE.gray;
  return (
    <div data-ph-id={phId} style={{
      display: 'flex', alignItems: 'center', gap: 14,
      background: 'var(--bg2)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)', padding: '14px 16px', marginBottom: 10,
    }}>
      <div style={{
        width: 38, height: 38, flexShrink: 0, borderRadius: '50%',
        background: 'var(--bg3)', color, display: 'flex',
        alignItems: 'center', justifyContent: 'center',
      }}><Ico name={ico} /></div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{title}</div>
        {sub && <div style={{ fontSize: 12.5, color: 'var(--text2)', marginTop: 2, lineHeight: 1.35 }}>{sub}</div>}
      </div>
      {cta && (
        <button className="btn btn-secondary btn-sm" onClick={onClick}
          style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>{cta}</button>
      )}
      {onDismiss && (
        <button type="button" onClick={onDismiss}
          data-ph-capture={`${phId}-dismiss`}
          aria-label={dismissLabel} title={dismissLabel}
          style={{
            flexShrink: 0, width: 28, height: 28, display: 'flex', alignItems: 'center',
            justifyContent: 'center', background: 'transparent', border: 'none',
            borderRadius: 6, color: 'var(--text3)', cursor: 'pointer', padding: 0,
          }}><Ico name="x" size={16} /></button>
      )}
    </div>
  );
}

// Делает конкретные числа в строке-приоритете кликабельными deep-link'ами «к сути».
// targets: [{ key, value, onClick }] — value-число ищется в тексте во всех типичных
// написаниях (raw / с пробелом-разделителем / nbsp / запятой), без lookbehind (Safari).
// Каждый сигнал линкуется один раз (первое вхождение), пересечения исключаются.
function escapeRe(s) { return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }

function numVariants(value) {
  const raw = String(value);
  const grouped = Number(value).toLocaleString('ru-RU'); // «5 634» (nbsp)
  return Array.from(new Set([
    raw,
    grouped,
    grouped.replace(/[\s\u00A0\u202F]/g, ' '),  // separator -> regular space
    Number(value).toLocaleString('en-US'), // «5,634»
  ])).filter(Boolean);
}

function linkifySegments(line, targets) {
  const matches = [];
  targets.forEach((tg) => {
    if (!tg || tg.value == null) return;
    numVariants(tg.value).forEach((v) => {
      const re = new RegExp(escapeRe(v), 'g');
      let m;
      while ((m = re.exec(line)) !== null) {
        const before = line[m.index - 1];
        const after = line[m.index + v.length];
        if ((before && /\d/.test(before)) || (after && /\d/.test(after))) continue;
        matches.push({ start: m.index, end: m.index + v.length, key: tg.key, onClick: tg.onClick });
      }
    });
  });
  matches.sort((a, b) => a.start - b.start || (b.end - b.start) - (a.end - a.start));
  const usedKey = new Set();
  const chosen = [];
  let lastEnd = -1;
  for (const m of matches) {
    if (m.start < lastEnd || usedKey.has(m.key)) continue;
    chosen.push(m); usedKey.add(m.key); lastEnd = m.end;
  }
  if (!chosen.length) return line;
  const segs = [];
  let i = 0;
  chosen.forEach((m, idx) => {
    if (m.start > i) segs.push(line.slice(i, m.start));
    segs.push(
      <button key={`n${idx}`} type="button" onClick={m.onClick}
        data-ph-id={`advisor-line-${m.key}`}
        style={{
          font: 'inherit', color: 'var(--accent)', fontWeight: 700, cursor: 'pointer',
          background: 'none', border: 'none', padding: 0, margin: 0,
          textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: 2,
        }}>{line.slice(m.start, m.end)}</button>
    );
    i = m.end;
  });
  if (i < line.length) segs.push(line.slice(i));
  return segs;
}

const ADV_CSS = `
.adv-spark{ display:inline-flex; animation: adv-pulse 1.4s ease-in-out infinite; }
@keyframes adv-pulse{ 0%,100%{ opacity:.55; transform:scale(1); } 50%{ opacity:1; transform:scale(1.14); } }
.adv-materialize{ animation: adv-mat .55s ease-out both; }
@keyframes adv-mat{ from{ opacity:0; filter:blur(6px); transform:translateY(2px); } to{ opacity:1; filter:blur(0); transform:none; } }
.adv-dust{ position:relative; display:flex; flex-direction:column; gap:8px; padding-top:4px; }
.adv-bar{ height:9px; border-radius:5px; background:linear-gradient(90deg, var(--border,#e0e0e8) 25%, var(--bg2,#f0f0f5) 50%, var(--border,#e0e0e8) 75%); background-size:300px 100%; animation: adv-shim 1.3s linear infinite; }
@keyframes adv-shim{ 0%{ background-position:-150px 0; } 100%{ background-position:150px 0; } }
.adv-particle{ position:absolute; width:4px; height:4px; border-radius:50%; background:var(--accent,#185fa5); opacity:0; animation: adv-gather 1.5s ease-in-out infinite; }
@keyframes adv-gather{ 0%{ opacity:0; transform:translate(var(--dx),var(--dy)) scale(.3); } 45%{ opacity:.95; } 100%{ opacity:0; transform:translate(0,0) scale(1); } }
`;

const DUST = [
  { dx: '-26px', dy: '-12px', d: '0s' },   { dx: '34px', dy: '-16px', d: '.25s' },
  { dx: '-40px', dy: '10px', d: '.5s' },    { dx: '50px', dy: '8px', d: '.15s' },
  { dx: '18px', dy: '16px', d: '.65s' },    { dx: '-14px', dy: '-18px', d: '.4s' },
];

function AdvisorStrip({ subtitle, text, loading }) {
  return (
    <div style={{
      display: 'flex', gap: 12, alignItems: 'flex-start',
      background: 'rgba(79,142,247,0.06)', border: '1px solid rgba(79,142,247,0.3)',
      borderRadius: 'var(--radius-lg)', padding: '14px 16px', marginBottom: 14,
    }}>
      <style>{ADV_CSS}</style>
      <span className={loading && !text ? 'adv-spark' : undefined} style={{ color: 'var(--accent)', flexShrink: 0, marginTop: 1 }}>
        <Ico name="sparkles" size={20} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--accent)', marginBottom: 3 }}>
          {subtitle}
        </div>
        {text ? (
          <div className="adv-materialize" style={{ fontSize: 15, lineHeight: 1.4, color: 'var(--text)' }}>{text}</div>
        ) : loading ? (
          <div className="adv-dust" aria-label="Собираю приоритет дня">
            <span className="adv-bar" style={{ width: '92%' }} />
            <span className="adv-bar" style={{ width: '58%' }} />
            {DUST.map((p, i) => (
              <span key={i} className="adv-particle"
                style={{ left: 6, top: 8, '--dx': p.dx, '--dy': p.dy, animationDelay: p.d }} />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

// Сигналы по оборудованию (Парк): по карточке на вид сигнала. Боевые — не гасятся.
// Данные — signals.equipment из GET /tasks/advisor (services/equipmentSignals.js);
// нет права или перемещение выключено — группы пустые, карточек нет вовсе.
const EQ_CARD = {
  long_breakdown:     { tone: 'danger', ico: 'alert', params: (th) => ({ hours: th.breakdown_hours }) },
  dispatched_unready: { tone: 'amber',  ico: 'alert', params: (th) => ({ days: th.unready_days }) },
  rsa_missing:        { tone: 'amber',  ico: 'file',  params: (th) => ({ days: th.rsa_days }) },
  // Осмотры. taskLink: одна единица → сразу в саму Работу-осмотр (там дефекты и «создать ремонт»).
  qa_blocked:         { tone: 'danger', ico: 'alert', taskLink: true, params: () => ({}) },
  open_defects:       { tone: 'amber',  ico: 'alert', taskLink: true, params: (th) => ({ days: th.open_defect_days }) },
  long_off_hire:      { tone: 'amber',  ico: 'clock', params: (th) => ({ days: th.off_hire_days }) },
  check_stale:        { tone: 'amber',  ico: 'clock', taskLink: true, params: (th) => ({ days: th.check_stale_days }) },
  overdue_task:       { tone: 'amber',  ico: 'clock', taskLink: true, params: () => ({}) },
  unit_unknown:       { tone: 'gray',   ico: 'eye',   fleetFilter: 'unknown', params: () => ({}) },
};

function EquipmentCards({ eq, t, lang, navigate, clickCard }) {
  const groups = (eq && eq.groups) || [];
  const th = (eq && eq.thresholds) || {};
  return groups.map((g) => {
    const cfg = EQ_CARD[g.kind];
    if (!cfg) return null; // вид, о котором этот клиент ещё не знает (старый бандл)
    const first = g.items[0] || {};
    const params = { count: g.count, ...cfg.params(th) };
    const open = () => clickCard(`equipment_${g.kind}`, () => {
      if (cfg.taskLink && g.count === 1 && first.property_id && first.task_id) {
        navigate(`/properties/${first.property_id}`, { state: { openTaskId: first.task_id } });
      } else if (g.count === 1) {
        navigate('/fleet', { state: { openEquipmentId: first.equipment_id } });
      } else {
        navigate('/fleet', cfg.fleetFilter ? { state: { fleetFilter: cfg.fleetFilter } } : undefined);
      }
    });
    return (
      <Card key={g.kind} tone={cfg.tone} ico={cfg.ico} phId={`advisor-card-equipment-${g.kind}`}
        title={t(`advisor.equipment.${g.kind}.title_${plf(g.count, lang)}`, params)}
        sub={g.count === 1 && first.name
          ? `${first.name}${first.property_name ? ` · ${first.property_name}` : ''}`
          : t(`advisor.equipment.${g.kind}.sub`, params)}
        cta={t('advisor.equipment.cta')}
        onClick={open} />
    );
  });
}

export default function AdminAdvisor({ onFilterDone, onFilterUninvoiced, onFilterUnviewed, onOpenAwaiting, onOpenAmcOverdue, onChanged, data: dataProp, controlled = false, lineLoading = false }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [fetched, setFetched] = useState(null);
  const [selfLoaded, setSelfLoaded] = useState(false);
  // Погашенное в этой сессии — прячем сразу, не дожидаясь ответа сервера.
  const [localDismissed, setLocalDismissed] = useState([]);
  // Сметы без НДС, по которым начисление только что создали из карточки.
  const [localCharged, setLocalCharged] = useState([]);
  const [charging, setCharging] = useState(null);
  const { can } = usePerm();

  // controlled=true → данными управляет родитель (страница Дашборда сделала фетч),
  // сам в API не ходим даже пока data ещё null. Иначе — автономный фетч.
  const data = controlled ? dataProp : fetched;
  const loaded = controlled ? (dataProp != null) : selfLoaded;

  useEffect(() => {
    if (controlled) return;
    let alive = true;
    const lang = i18n.language === 'en' ? 'en' : 'ru';
    api.get(`/tasks/advisor?lang=${lang}`)
      .then(r => { if (alive) { setFetched(r); setSelfLoaded(true); } })
      .catch(() => { if (alive) setSelfLoaded(true); });
    return () => { alive = false; };
  }, [i18n.language, controlled]);

  useEffect(() => {
    if (loaded && data && data.state && data.state !== 'cold') {
      Analytics.advisorViewed(data.state);
    }
  }, [loaded, data]);

  if (!loaded || !data || !data.state || data.state === 'cold') return null;

  const s = data.signals || {};
  const clickCard = (key, fn) => { Analytics.advisorCardClicked(key, data.state); fn && fn(); };
  const dismissed = [...(data.dismissed || []), ...localDismissed];
  // «Работа выполнена — начислить по смете без НДС»: один клик создаёт ручное
  // начисление на остаток сметы в Работе, поступление отметят в блоке «Доходы».
  const chargeQuote = async (item) => {
    setCharging(item.quote_id);
    try {
      await api.post(`/quotes/${item.quote_id}/manual-charge`, {});
      Analytics.quoteManualCharged({ place: 'advisor' });
      toast.success(t('advisor.quoteCharge.done'));
      setLocalCharged((prev) => [...prev, item.quote_id]);
      if (onChanged) onChanged();
    } catch (e) { toast.error(e.message); }
    finally { setCharging(null); }
  };
  const dismissCard = (key) => {
    track('advisor_card_dismissed', { key, state: data.state });
    setLocalDismissed((prev) => [...prev, key]);
    // Не удалось сохранить — вернём карточку, чтобы не врать, что она погашена.
    api.post('/tasks/advisor/dismiss', { key })
      .catch(() => setLocalDismissed((prev) => prev.filter((k) => k !== key)));
  };

  // Плашка идёт над карточками советника: он даёт РЕКОМЕНДАЦИИ, по которым
  // человек действует, и «маржа просела» по половине портфеля — это другой
  // вывод, чем по всему. Рисуем компактно, чтобы не спорить с самим советником
  // за внимание.
  const cityNote = <CityLimitNote value={data.city_limit} inline />;

  // ── WARMING: мост к первым деньгам ─────────────────────────────────────────
  if (data.state === 'warming') {
    const done = s.counts?.tasks_done || 0;
    const showInvoice = !dismissed.includes('warming_invoice');
    const showZoho = !dismissed.includes('warming_zoho');
    // Парк живёт своей жизнью: поломка у арендатора важнее, погашены ли советы про инвойс.
    const eqGroups = (s.equipment && s.equipment.groups) || [];
    if (!showInvoice && !showZoho && !eqGroups.length) return null;
    return (
      <div style={{ padding: '0 16px', marginTop: 12 }}>
        {cityNote && <div style={{ marginBottom: 8 }}>{cityNote}</div>}
        {/* Строка дня целиком про «нет инвойса» — без карточки инвойса она висит в воздухе. */}
        {showInvoice && <AdvisorStrip subtitle={t('advisor.label')} text={t(`advisor.warming.line_${plf(done, i18n.language)}`, { count: done })} />}
        {showInvoice && <Card tone="danger" ico="file" phId="advisor-warming-invoice"
          title={t('advisor.warming.invoiceTitle')}
          sub={t(`advisor.warming.invoiceSub_${plf(done, i18n.language)}`, { count: done })}
          cta={t('advisor.warming.invoiceCta')}
          dismissLabel={t('advisor.dismiss')} onDismiss={() => dismissCard('warming_invoice')}
          onClick={() => clickCard('warming_invoice', () => (onFilterDone ? onFilterDone() : navigate('/tasks')))} />}
        {showZoho && <Card tone="info" ico="link" phId="advisor-warming-zoho"
          title={t('advisor.warming.zohoTitle')}
          sub={t('advisor.warming.zohoSub')}
          cta={t('advisor.warming.zohoCta')}
          dismissLabel={t('advisor.dismiss')} onDismiss={() => dismissCard('warming_zoho')}
          onClick={() => clickCard('warming_zoho', () => navigate('/settings'))} />}
        <EquipmentCards eq={s.equipment} t={t} lang={i18n.language} navigate={navigate} clickCard={clickCard} />
      </div>
    );
  }

  // ── LIVE: денежный слой ────────────────────────────────────────────────────
  const leak = s.revenueLeak || { count: 0 };
  const awaitP = s.awaitingPayment || { count: 0, total: 0, oldest_days: 0 };
  const exp = s.pendingExpenses || { count: 0, total: 0 };
  const amcOv = s.amcOverduePayments || { count: 0 };
  const bdays = s.birthdays || [];
  const docs = s.expiringDocs || [];
  const unviewed = s.unviewedAssignments || { count: 0 };
  const emerg = s.emergencyNoReaction || { count: 0, items: [] };
  const takenNoWork = s.takenNoWork || { count: 0, items: [] };
  const dueSoon = s.dueSoon || { count: 0, overdue: 0, items: [] };
  const readyNotClosed = s.readyNotClosed || { count: 0, items: [] };
  const ctrl = s.control || {};
  const marginDrop = ctrl.marginDrop || { count: 0, items: [] };
  const overrun = ctrl.overrun || { count: 0, items: [] };
  const phaseOverdue = ctrl.phaseOverdue || { count: 0, items: [] };
  const planDev = ctrl.planDeviation || { count: 0, items: [] };
  const awaitFu = s.awaitingFollowUp || { count: 0, items: [] };
  const cur = awaitP.currency || 'AED';
  const fmt = (n) => Number(n || 0).toLocaleString(i18n.language === 'en' ? 'en-US' : 'ru-RU');
  // Суммы по валютам (без сложения разных валют): «850 AED · 7 777 RUB».
  const moneyByCur = (byCurrency, total, curCode) => {
    const list = (Array.isArray(byCurrency) && byCurrency.length) ? byCurrency : [{ currency: curCode || 'AED', total }];
    const parts = list.filter(x => x.total).map(x => formatMajor(x.total, x.currency, i18n.language));
    return parts.length ? parts.join(' · ') : formatMajor(0, curCode || 'AED', i18n.language);
  };
  const awaitAmounts = moneyByCur(awaitP.byCurrency, awaitP.total, cur);
  const expAmounts = moneyByCur(exp.byCurrency, exp.total, exp.currency);

  // Клик по числу в строке-приоритете → к источнику цифры (та же навигация, что у карточек).
  const goExpenses = () => navigate('/expenses', { state: { expensesPeriod: 'all', expensesStatus: 'pending_review' } });
  const lineTargets = [
    { key: 'leak', value: leak.count > 0 ? leak.count : null, onClick: () => { Analytics.advisorLineClicked('leak', data.state); (onFilterUninvoiced ? onFilterUninvoiced() : navigate('/tasks')); } },
    { key: 'awaiting', value: awaitP.total > 0 ? awaitP.total : null, onClick: () => { Analytics.advisorLineClicked('awaiting', data.state); (onOpenAwaiting ? onOpenAwaiting() : navigate('/tasks')); } },
    { key: 'awaiting_count', value: awaitP.count > 0 ? awaitP.count : null, onClick: () => { Analytics.advisorLineClicked('awaiting', data.state); (onOpenAwaiting ? onOpenAwaiting() : navigate('/tasks')); } },
    { key: 'expenses', value: exp.total > 0 ? exp.total : null, onClick: () => { Analytics.advisorLineClicked('expenses', data.state); goExpenses(); } },
    { key: 'expenses_count', value: exp.count > 0 ? exp.count : null, onClick: () => { Analytics.advisorLineClicked('expenses', data.state); goExpenses(); } },
  ];
  const lineNode = data.line ? linkifySegments(data.line, lineTargets) : null;

  return (
    // ym-hide-content — советник показывает суммы: утечки, ждущие оплаты,
    // расходы. Не декоративный класс, см. metrika_migration_plan.md §7.
    <div className="ym-hide-content" style={{ padding: '0 16px', marginTop: 12 }} data-ph-id="advisor-live">
      {cityNote && <div style={{ marginBottom: 8 }}>{cityNote}</div>}
      {(lineNode || lineLoading) && (
        <AdvisorStrip subtitle={t('advisor.liveLabel')} text={lineNode} loading={lineLoading} />
      )}

      {emerg.count > 0 && (
        <Card tone="danger" ico="alert" phId="advisor-card-emergency"
          title={t(`advisor.emergency.title_${plf(emerg.count, i18n.language)}`, { count: emerg.count })}
          sub={emerg.items[0] && emerg.items[0].title ? t('advisor.emergency.subItem', { title: emerg.items[0].title }) : t('advisor.emergency.sub')}
          cta={t('advisor.emergency.cta')}
          onClick={() => clickCard('emergency', () => navigate('/requests', { state: { openRequestId: emerg.items[0] && emerg.items[0].id } }))} />
      )}
      <EquipmentCards eq={s.equipment} t={t} lang={i18n.language} navigate={navigate} clickCard={clickCard} />
      {leak.count > 0 && (
        <Card tone="danger" ico="alert" phId="advisor-card-leak"
          title={t(`advisor.leak.title_${plf(leak.count, i18n.language)}`, { count: leak.count })}
          sub={t('advisor.leak.sub')}
          cta={t('advisor.leak.cta')}
          onClick={() => clickCard('leak', () => (onFilterUninvoiced ? onFilterUninvoiced() : navigate('/tasks')))} />
      )}
      {can('income.create') && (s.quoteCharge?.items || []).filter(i => !localCharged.includes(i.quote_id)).map(item => (
        <Card key={item.quote_id} tone="amber" ico="file" phId="advisor-card-quote-charge"
          title={t('advisor.quoteCharge.title', {
            task: item.task_title || '—', amount: formatMajor(item.amount, item.currency, i18n.language),
          })}
          sub={t('advisor.quoteCharge.sub', { quote: item.label })}
          cta={charging === item.quote_id ? `${t('common.loading')}…` : t('advisor.quoteCharge.cta')}
          onClick={() => { if (charging == null) clickCard('quote_charge', () => chargeQuote(item)); }} />
      ))}
      {awaitP.count > 0 && (
        <Card tone="amber" ico="clock" phId="advisor-card-awaiting"
          title={t(`advisor.awaiting.title_${plf(awaitP.count, i18n.language)}`, { count: awaitP.count, amounts: awaitAmounts, total: fmt(awaitP.total), cur })}
          sub={awaitP.oldest_days > 0 ? t('advisor.awaiting.subAge', { days: awaitP.oldest_days }) : t('advisor.awaiting.sub')}
          cta={t('advisor.awaiting.cta')}
          onClick={() => clickCard('awaiting', () => (onOpenAwaiting ? onOpenAwaiting() : navigate('/tasks')))} />
      )}
      {exp.count > 0 && (
        <Card tone="info" ico="receipt" phId="advisor-card-expenses"
          title={t(`advisor.expenses.title_${plf(exp.count, i18n.language)}`, { count: exp.count, amounts: expAmounts, total: fmt(exp.total), cur })}
          sub={t('advisor.expenses.sub')}
          cta={t('advisor.expenses.cta')}
          onClick={() => clickCard('expenses', () => navigate('/expenses', { state: { expensesPeriod: 'all', expensesStatus: 'pending_review' } }))} />
      )}
      {amcOv.count > 0 && (
        <Card tone="purple" ico="clock" phId="advisor-card-amc-overdue"
          title={t(`advisor.amc.title_${plf(amcOv.count, i18n.language)}`, { count: amcOv.count })}
          sub={t('advisor.amc.sub')}
          cta={t('advisor.amc.cta')}
          onClick={() => clickCard('amc_overdue', () => (onOpenAmcOverdue ? onOpenAmcOverdue() : navigate('/properties')))} />
      )}
      {marginDrop.count > 0 && (
        <Card tone="danger" ico="alert" phId="advisor-card-margin-drop"
          title={marginDrop.count === 1
            ? t('advisor.marginDrop.titleOne', { name: marginDrop.items[0].project_title, pp: marginDrop.items[0].drop_pp })
            : t(`advisor.marginDrop.titleMany_${plf(marginDrop.count, i18n.language)}`, { count: marginDrop.count })}
          sub={t('advisor.marginDrop.sub')}
          cta={t('advisor.marginDrop.cta')}
          onClick={() => clickCard('margin_drop', () => navigate(`/projects/${marginDrop.items[0].project_id}`))} />
      )}
      {overrun.count > 0 && (
        <Card tone="amber" ico="alert" phId="advisor-card-overrun"
          title={overrun.count === 1
            ? t('advisor.overrun.titleOne', { name: overrun.items[0].project_title })
            : t(`advisor.overrun.titleMany_${plf(overrun.count, i18n.language)}`, { count: overrun.count })}
          sub={t('advisor.overrun.sub')}
          cta={t('advisor.overrun.cta')}
          onClick={() => clickCard('overrun', () => navigate(`/projects/${overrun.items[0].project_id}`))} />
      )}
      {phaseOverdue.count > 0 && (
        <Card tone="danger" ico="clock" phId="advisor-card-phase-overdue"
          title={phaseOverdue.count === 1
            ? t('advisor.phaseOverdue.titleOne', { phase: phaseOverdue.items[0].phase_title, days: phaseOverdue.items[0].delay_days })
            : t(`advisor.phaseOverdue.titleMany_${plf(phaseOverdue.count, i18n.language)}`, { count: phaseOverdue.count })}
          sub={t('advisor.phaseOverdue.sub')}
          cta={t('advisor.phaseOverdue.cta')}
          onClick={() => clickCard('phase_overdue', () => navigate(`/projects/${phaseOverdue.items[0].project_id}`))} />
      )}
      {planDev.count > 0 && (
        <Card tone="danger" ico="clock" phId="advisor-card-plan-deviation"
          title={planDev.count === 1
            ? t('advisor.planDeviation.titleOne', { name: planDev.items[0].project_title })
            : t(`advisor.planDeviation.titleMany_${plf(planDev.count, i18n.language)}`, { count: planDev.count })}
          sub={planDev.count === 1 ? t(planDev.items[0].project_slip_days > 0 ? 'advisor.planDeviation.subOne' : 'advisor.planDeviation.subOneNoFinish', {
            late: planDev.items[0].late, days: planDev.items[0].worst_days, added: planDev.items[0].added, removed: planDev.items[0].removed,
            finish: planDev.items[0].project_slip_days,
          }) : t('advisor.planDeviation.sub')}
          cta={t('advisor.planDeviation.cta')}
          onClick={() => clickCard('plan_deviation', () => navigate(`/projects/${planDev.items[0].project_id}`, { state: { returnView: 'gantt' } }))} />
      )}
      {awaitFu.count > 0 && (
        <Card tone="amber" ico="clock" phId="advisor-card-awaiting-follow-up"
          title={t(`advisor.awaitingFollowUp.title_${plf(awaitFu.count, i18n.language)}`, { count: awaitFu.count })}
          sub={awaitFu.items[0] && awaitFu.items[0].title ? t('advisor.awaitingFollowUp.subItem', { title: awaitFu.items[0].title, days: awaitFu.items[0].days_waiting }) : t('advisor.awaitingFollowUp.sub')}
          cta={t('advisor.awaitingFollowUp.cta')}
          onClick={() => clickCard('awaiting_follow_up', () => navigate('/tasks', { state: { returnFilter: 'pending', statusSub: 'awaiting' } }))} />
      )}
      {takenNoWork.count > 0 && (
        <Card tone="amber" ico="clock" phId="advisor-card-taken-no-work"
          title={t(`advisor.takenNoWork.title_${plf(takenNoWork.count, i18n.language)}`, { count: takenNoWork.count })}
          sub={takenNoWork.items[0] && takenNoWork.items[0].title ? t('advisor.takenNoWork.subItem', { title: takenNoWork.items[0].title }) : t('advisor.takenNoWork.sub')}
          cta={t('advisor.takenNoWork.cta')}
          onClick={() => clickCard('taken_no_work', () => navigate('/requests', { state: { openRequestId: takenNoWork.items[0] && takenNoWork.items[0].id } }))} />
      )}
      {dueSoon.count > 0 && (
        <Card tone={dueSoon.overdue > 0 ? 'danger' : 'amber'} ico="clock" phId="advisor-card-due-soon"
          title={t(`advisor.dueSoon.title_${plf(dueSoon.count, i18n.language)}`, { count: dueSoon.count })}
          sub={dueSoon.overdue > 0 ? t('advisor.dueSoon.subOverdue', { count: dueSoon.overdue }) : t('advisor.dueSoon.sub')}
          cta={t('advisor.dueSoon.cta')}
          onClick={() => clickCard('due_soon', () => navigate('/requests', { state: { openRequestId: dueSoon.items[0] && dueSoon.items[0].id } }))} />
      )}
      {readyNotClosed.count > 0 && (
        <Card tone="info" ico="link" phId="advisor-card-ready-not-closed"
          title={t(`advisor.readyNotClosed.title_${plf(readyNotClosed.count, i18n.language)}`, { count: readyNotClosed.count })}
          sub={t('advisor.readyNotClosed.sub')}
          cta={t('advisor.readyNotClosed.cta')}
          onClick={() => clickCard('ready_not_closed', () => navigate('/requests', { state: { openRequestId: readyNotClosed.items[0] && readyNotClosed.items[0].id } }))} />
      )}
      {unviewed.count > 0 && (
        <Card tone="amber" ico="eye" phId="advisor-card-unviewed"
          title={t(`advisor.unviewed.title_${plf(unviewed.count, i18n.language)}`, { count: unviewed.count })}
          sub={t('advisor.unviewed.sub')}
          cta={t('advisor.unviewed.cta')}
          onClick={() => clickCard('unviewed', () => (onFilterUnviewed ? onFilterUnviewed() : navigate('/tasks')))} />
      )}
      {bdays.length > 0 && (
        <Card tone="teal" ico="gift" phId="advisor-card-birthday"
          title={t('advisor.birthday.title', { name: bdays[0].name })}
          sub={bdays[0].in_days === 0 ? t('advisor.birthday.subToday') : t('advisor.birthday.sub', { days: bdays[0].in_days })}
          cta={t('advisor.birthday.cta')}
          onClick={() => clickCard('birthday', () => navigate(bdays[0].id ? `/contacts/${bdays[0].id}` : '/contacts'))} />
      )}
      {docs.length > 0 && (
        <Card tone="gray" ico="file" phId="advisor-card-docs"
          title={t(`advisor.docs.title_${plf(docs.length, i18n.language)}`, { count: docs.length })}
          sub={t('advisor.docs.sub')}
          cta={t('advisor.docs.cta')}
          onClick={() => clickCard('docs', () => navigate('/settings'))} />
      )}
    </div>
  );
}
